/**
 * Servicio para el endpoint /inventarios
 *
 * Contrato real del backend: ../api/docs/api/inventarios.md. "Mis inventarios"
 * no tiene endpoint propio: es GET /inventarios, y para un usuario no ADMIN el
 * backend ignora el preparadorId recibido y fuerza el del JWT.
 *
 * DECISION: la búsqueda de artículos (buscarArticulos) usa el buscador de
 * productos existente (ProductService.getProducts), el mismo de traspasos y
 * peticiones. Su Stocks2Models da el stock actual por nave, solo informativo:
 * el stock teórico lo congela el backend al registrar el conteo en cada nave.
 */

import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";
import ProductService from "../../../services/product.service";
import { normalizarCodigoAlmacen } from "../utils/formatters";
import { ESTADOS_SIN_REVISAR } from "../constants";

const RUTA_BASE = "inventarios";

/**
 * Extrae un mensaje de error legible de una respuesta fallida de Axios.
 * El backend responde {status, code, message, details}; en los errores de
 * validación `message` es el genérico "Parametros incorrectos" y el motivo
 * real viene en details[].msg, así que se prefiere ese.
 * @param {Object} error - Error capturado de Axios
 * @param {string} fallback - Mensaje genérico si no hay ninguna forma reconocida
 * @returns {string} Mensaje a mostrar al usuario
 */
const extractErrorMessage = (error, fallback) => {
    const cuerpo = error.response?.data;
    const detalles = Array.isArray(cuerpo?.details) ? cuerpo.details.map((detalle) => detalle?.msg).filter(Boolean) : [];
    if (detalles.length > 0) return detalles.join(" ");
    return cuerpo?.message || error.message || fallback;
};

/**
 * GET/POST/PUT/PATCH/DELETE contra la API: desenvuelve response.data.data
 * y relanza los errores con un mensaje legible. Nunca devuelve un array vacío
 * ni null ante un fallo: el usuario lo leería como "no hay inventarios" en
 * vez de "no se pudo consultar".
 * @param {Function} peticion - Función que devuelve la promesa de Axios
 * @param {string} descripcion - Qué se estaba haciendo, para el mensaje de error
 * @returns {Promise<*>} Contenido de `data`, tal y como llega
 */
const ejecutar = async (peticion, descripcion) => {
    try {
        const res = await peticion();
        return res.data?.data ?? res.data ?? null;
    } catch (error) {
        console.error(`Error al ${descripcion}:`, error);
        throw new Error(extractErrorMessage(error, `No se pudo ${descripcion}`));
    }
};

// Producto de Eurowin (CODIGO, NOMBRE, BarrasModels, Stocks2Models) -> artículo del feature
const aArticulo = (producto) => ({
    referencia: producto.CODIGO,
    nombre: producto.NOMBRE,
    ean: producto.BarrasModels?.[0]?.BARRAS ?? "",
    ubicacion: String(producto.UBICACION ?? "").trim(),
    stockPorAlmacen: Object.fromEntries(
        (producto.Stocks2Models ?? []).map((stock) => [normalizarCodigoAlmacen(stock.ALMACEN), Number(stock.FINAL ?? 0)])
    ),
});

/**
 * Valida en el cliente que la cantidad contada sea un entero >= 0, ANTES de
 * llamar al servicio (el backend también valida), para no gastar una
 * petición en un valor que ya se sabe inválido.
 * @param {*} cantidad - Valor a validar
 * @throws {Error} Si no es un entero >= 0
 */
const validarCantidadContada = (cantidad) => {
    if (!Number.isInteger(cantidad) || cantidad < 0) {
        throw new Error("La cantidad contada debe ser un número entero mayor o igual que 0.");
    }
};

const urlInventario = (id, sufijo = "") => HostService.getHost() + RUTA_BASE + "/" + id + sufijo;

/**
 * Servicio de listas de inventario (conteo asignado)
 */
class InventariosService {
    /**
     * GET /inventarios - listas de inventario que cumplen los filtros, sin paginar
     * (el backend limita a 1000 filas, de la más reciente a la más antigua)
     * @param {Object} [filtros] - Filtros opcionales
     * @param {string} [filtros.estado] - INVENTARIO_ESTADO
     * @param {number} [filtros.preparadorId] - Id del preparador asignado (solo lo respeta para ADMIN)
     * @param {string} [filtros.fechaDesde] - Creadas desde este día, incluido (YYYY-MM-DD)
     * @param {string} [filtros.fechaHasta] - Creadas hasta este día, incluido (YYYY-MM-DD)
     * @returns {Promise<Array>} Listas de inventario en camelCase
     */
    static async listar(filtros = {}) {
        const params = Object.fromEntries(
            Object.entries(filtros).filter(([, valor]) => valor !== undefined && valor !== null && valor !== "")
        );
        return ejecutar(
            () => axios.get(HostService.getHost() + RUTA_BASE, { ...AuthService.setToken(), params }),
            "listar los inventarios"
        );
    }

    /**
     * Número de inventarios sin revisar (PENDIENTE, EN_CURSO o COMPLETADO), para el
     * contador del header del admin.
     *
     * DECISION: el backend no tiene endpoint de conteo (traspasos sí, TraspasosService.count):
     * se pide el listado de cada estado y se suman. Por estado, y no el listado entero menos
     * los revisados, porque el listado se corta en 1000 filas y los revisados se acumulan.
     * @returns {Promise<number>} Inventarios sin revisar
     */
    static async contarSinRevisar() {
        const porEstado = await Promise.all(ESTADOS_SIN_REVISAR.map((estado) => InventariosService.listar({ estado })));
        return porEstado.reduce((total, lista) => total + (Array.isArray(lista) ? lista.length : 0), 0);
    }

    /**
     * GET /inventarios del preparador en sesión. Se envía su preparadorId para
     * que un ADMIN vea también solo las suyas; para el resto de roles el
     * backend ya lo fuerza desde el JWT
     * @param {Object} [filtros] - estado, fechaDesde y fechaHasta
     * @returns {Promise<Array>} Listas asignadas al usuario actual
     * @throws {Error} Si no hay sesión de usuario activa
     */
    static async listarMisInventarios(filtros = {}) {
        const usuarioId = AuthService.getCurrentUser()?.id;
        if (usuarioId == null) throw new Error("No hay sesión de usuario activa.");

        return InventariosService.listar({ ...filtros, preparadorId: usuarioId });
    }

    /**
     * GET /inventarios/:id - obtiene una lista de inventario por id
     * @param {number} id - Id de la lista
     * @returns {Promise<Object>} La lista de inventario con sus líneas
     */
    static async getById(id) {
        return ejecutar(() => axios.get(urlInventario(id), AuthService.setToken()), `obtener el inventario ${id}`);
    }

    /**
     * Busca artículos en el buscador de productos (GET productos?search=), para
     * añadirlos como línea a una lista de inventario
     * @param {string} query - Texto de búsqueda (código, nombre o código de barras)
     * @returns {Promise<Array<{referencia: string, nombre: string, ean: string, stockPorAlmacen: Object<string, number>}>>}
     *   Artículos coincidentes; stockPorAlmacen indexado por código de almacén con padding ("01")
     */
    static async buscarArticulos(query) {
        const productos = await ProductService.getProducts(query);
        return (productos ?? []).map(aArticulo);
    }

    /**
     * Stock actual por almacén y ubicación de cada referencia, del maestro de
     * artículos. Las líneas no traen ninguno de los dos: el stock, para que el
     * preparador elija dónde contar (solo traen el snapshot del almacén ya contado),
     * y la ubicación, para encontrar el artículo.
     * Una búsqueda por referencia, porque el buscador de productos no admite varias
     * @param {Array<string>} referencias - Referencias de las líneas
     * @returns {Promise<Object<string, {stockPorAlmacen: Object<string, number>, ubicacion: string}>>}
     *   { referencia: { stockPorAlmacen: { "01": 50, ... }, ubicacion: "I12A" } }; una
     *   referencia sin coincidencia exacta no aparece
     */
    static async getArticulos(referencias) {
        const unicas = [...new Set(referencias)];
        const resultados = await Promise.all(unicas.map((referencia) => InventariosService.buscarArticulos(referencia)));
        return Object.fromEntries(
            unicas.flatMap((referencia, i) => {
                const articulo = resultados[i].find((item) => item.referencia === referencia);
                return articulo ? [[referencia, { stockPorAlmacen: articulo.stockPorAlmacen, ubicacion: articulo.ubicacion }]] : [];
            })
        );
    }

    /**
     * POST /inventarios - crea y asigna una nueva lista de inventario (el backend
     * notifica al preparador por push). Solo ADMIN
     * @param {Object} datos - Datos de la lista (sin almacén: la nave se elige al contar)
     * @param {number} datos.preparadorId - Id del preparador (rol admin, reponedor o almacén; el backend guarda su username)
     * @param {Array<{referencia: string, nombre: string, ean: string}>} datos.lineas - Artículos a contar
     * @param {string} [datos.fechaLimite] - Fecha límite opcional (YYYY-MM-DD)
     * @param {string} [datos.observaciones] - Observaciones opcionales
     * @returns {Promise<Object>} La lista creada, en estado PENDIENTE
     */
    static async crear(datos) {
        return ejecutar(() => axios.post(HostService.getHost() + RUTA_BASE, datos, AuthService.setToken()), "crear el inventario");
    }

    /**
     * PUT /inventarios/:id - reemplaza una lista PENDIENTE (líneas renumeradas desde 1). Solo ADMIN
     * @param {number} id - Id de la lista
     * @param {Object} datos - Mismos campos que crear()
     * @returns {Promise<Object>} La lista actualizada
     */
    static async actualizar(id, datos) {
        return ejecutar(() => axios.put(urlInventario(id), datos, AuthService.setToken()), `actualizar el inventario ${id}`);
    }

    /**
     * PATCH /inventarios/:id/lineas/:lineaId - crea o actualiza el conteo de una
     * línea en una nave, sin tocar las demás naves. Solo el preparador asignado;
     * el primer conteo pasa la lista a EN_CURSO
     * @param {number} id - Id de la lista
     * @param {number} lineaId - Número de línea dentro de la lista (campo `id` de la línea)
     * @param {number} cantidad - Cantidad contada (entero >= 0)
     * @param {string} almacen - Código de almacén donde se cuenta
     * @returns {Promise<Object>} La lista actualizada
     */
    static async registrarConteo(id, lineaId, cantidad, almacen) {
        validarCantidadContada(cantidad);

        if (!almacen || typeof almacen !== 'string' || almacen.trim().length === 0) {
            throw new Error("El almacén es obligatorio para registrar el conteo de una línea.");
        }

        return ejecutar(
            () => axios.patch(
                urlInventario(id, "/lineas/" + lineaId),
                { cantidadContada: cantidad, almacen: almacen.trim() },
                AuthService.setToken()
            ),
            `registrar el conteo de la línea ${lineaId}`
        );
    }

    /**
     * DELETE /inventarios/:id/lineas/:lineaId/conteos/:almacen - borra el conteo de
     * una línea en una nave (contada por error), sin tocar las demás naves ni el
     * estado de la lista. Solo el preparador asignado
     * @param {number} id - Id de la lista
     * @param {number} lineaId - Número de línea dentro de la lista
     * @param {string} almacen - Código de almacén del conteo a borrar
     * @returns {Promise<Object>} La lista actualizada
     */
    static async eliminarConteo(id, lineaId, almacen) {
        if (!almacen || typeof almacen !== "string" || almacen.trim().length === 0) {
            throw new Error("El almacén es obligatorio para eliminar el conteo de una línea.");
        }
        return ejecutar(
            () => axios.delete(urlInventario(id, `/lineas/${lineaId}/conteos/${encodeURIComponent(almacen.trim())}`), AuthService.setToken()),
            `eliminar el conteo de la línea ${lineaId} en el almacén ${almacen}`
        );
    }

    /**
     * PATCH /inventarios/:id/finalizar - finaliza una lista con todas sus líneas contadas
     * @param {number} id - Id de la lista
     * @returns {Promise<Object>} La lista actualizada, en estado COMPLETADO
     */
    static async finalizar(id) {
        return ejecutar(() => axios.patch(urlInventario(id, "/finalizar"), {}, AuthService.setToken()), `finalizar el inventario ${id}`);
    }

    /**
     * PATCH /inventarios/:id/revisar - marca como revisada una lista COMPLETADO. Solo ADMIN
     * @param {number} id - Id de la lista
     * @returns {Promise<Object>} La lista actualizada, en estado REVISADO
     */
    static async revisar(id) {
        return ejecutar(
            () => axios.patch(urlInventario(id, "/revisar"), {}, AuthService.setToken()),
            `marcar como revisado el inventario ${id}`
        );
    }

    /**
     * DELETE /inventarios/:id - elimina una lista PENDIENTE. Solo ADMIN
     * @param {number} id - Id de la lista
     * @returns {Promise<{id: number}>} Id de la lista eliminada
     */
    static async eliminar(id) {
        return ejecutar(() => axios.delete(urlInventario(id), AuthService.setToken()), `eliminar el inventario ${id}`);
    }
}

export default InventariosService;
